import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Sparkles, X } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pattern Discovery card — surfaces statistically meaningful correlations
 * Nanouk has found in the user's last 30 days of data. One at a time,
 * dismissable, fresh ones bubble up automatically every 3 days.
 */
export function PatternDiscoveryCard() {
    const { language } = useLanguage();
    const [discoveries, setDiscoveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acking, setAcking] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/discoveries/latest?language=${language}`);
                if (cancelled) return;
                setDiscoveries(res.data?.discoveries || []);
            } catch (_e) { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [language]);

    const handleAck = async (id) => {
        setAcking(true);
        try {
            await axios.post(`${API}/discoveries/${id}/acknowledge`, {});
            setDiscoveries(prev => prev.filter(d => d.id !== id));
        } catch (_e) { /* non-fatal */ }
        finally { setAcking(false); }
    };

    if (loading || discoveries.length === 0) return null;

    const d = discoveries[0]; // show one at a time
    const corrPct = Math.round(Math.abs(d.correlation) * 100);

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={d.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.4 }}
                data-testid="pattern-discovery-card"
            >
                <Card className="overflow-hidden border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-500/10 via-card to-card">
                    <CardContent className="p-4 relative">
                        <button
                            type="button"
                            onClick={() => handleAck(d.id)}
                            disabled={acking}
                            aria-label={language === 'de' ? 'Erkenntnis bestätigt' : 'Got it'}
                            data-testid="discovery-dismiss"
                            className="absolute top-2 right-2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-start gap-3 pr-6">
                            <div className="shrink-0 w-9 h-9 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-fuchsia-500" />
                            </div>
                            <div className="min-w-0 flex-1 space-y-1">
                                <p className="text-[10px] uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-400 font-semibold">
                                    {language === 'de' ? '✨ Magic Moment · Muster entdeckt' : "✨ Magic moment · Pattern found"}
                                </p>
                                <h3 className="font-display font-bold text-sm leading-tight" data-testid="discovery-headline">
                                    {d.headline}
                                </h3>
                                <p className="text-xs text-muted-foreground leading-snug" data-testid="discovery-body">
                                    {d.body}
                                </p>
                                <p className="text-[10px] text-muted-foreground/70 pt-1">
                                    {language === 'de'
                                        ? `Korrelation: ${d.direction === 'positive' ? '+' : '−'}${corrPct} % · ${d.sample_size} Tage Datenbasis`
                                        : `Correlation: ${d.direction === 'positive' ? '+' : '−'}${corrPct}% · ${d.sample_size} days of data`}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
}
